import { SearchBox } from '@/components/SearchBox'
import { SiteFooter } from '@/components/SiteFooter'
import { SiteHeader } from '@/components/SiteHeader'
import {
  getCategoryLabel,
  getCategoryMeta,
  sortCategories,
  START_HERE_SLUGS,
} from '@/lib/categories'
import { APP_URL, SITE_NAME, SITE_URL, STATUS_URL } from '@/lib/config'
import { useHelpIndex } from '@/lib/data'
import { useHead } from '@/lib/head'
import {
  ArrowRightIcon,
  ChatCircleDotsIcon,
  LifebuoyIcon,
  PulseIcon,
} from '@phosphor-icons/react'
import { Link } from 'react-router-dom'

import { groupHelpCategories } from '@scrambl/shared/help/help-sections'

export default function HomePage() {
  const { index, indexError } = useHelpIndex()

  useHead({
    title: `${SITE_NAME} — guides and answers for Scrambl`,
    description:
      'Guides, concepts, and answers for every part of Scrambl — from your first workspace to advanced workflows.',
    path: '/',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${SITE_URL}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  })

  const categories = index ? sortCategories(index.categories) : []
  const startHere = categories.filter(row =>
    START_HERE_SLUGS.includes(row.slug),
  )
  const areas = categories.filter(row => !START_HERE_SLUGS.includes(row.slug))
  const areaGroups = groupHelpCategories(areas)
  const articleCount = index?.articles.length ?? 0

  return (
    <div className="shell">
      <SiteHeader />

      <section className="hero reveal">
        <div className="hero-eyebrow">Help Center</div>
        <h1>How can we help?</h1>
        <p className="hero-sub">
          Guides, concepts, and answers for every part of Scrambl.
        </p>
        <div className="hero-search">
          <SearchBox autoFocus />
        </div>
        {index ? (
          <p className="hero-meta">
            {articleCount} articles across {categories.length} product areas
          </p>
        ) : null}
      </section>

      {indexError && !index ? (
        <div className="notice notice-error">
          We couldn't load the help library right now. Refresh to try again, or
          check <a href={STATUS_URL}>status.scrambl.com</a>.
        </div>
      ) : null}

      {startHere.length > 0 ? (
        <section className="section reveal" style={{ animationDelay: '80ms' }}>
          <div className="section-label">
            <span>Start here</span>
          </div>
          <div className="start-grid">
            {startHere.map(row => {
              const meta = getCategoryMeta(row.slug)
              const Icon = meta.icon
              const firstArticles = index!.articles
                .filter(article => article.moduleSlug === row.slug)
                .slice(0, 3)
              return (
                <div
                  key={row.slug}
                  className={`card start-card hue-${meta.hue}`}
                >
                  <div className="start-card-head">
                    <span className="category-icon">
                      <Icon size={22} weight="duotone" />
                    </span>
                    <div>
                      <h2>{getCategoryLabel(row.slug)}</h2>
                      <p>{meta.description}</p>
                    </div>
                  </div>
                  <ul className="start-card-links">
                    {firstArticles.map(article => (
                      <li key={article.id}>
                        <Link
                          to={`/articles/${encodeURIComponent(article.id)}`}
                        >
                          {article.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    className="start-card-more"
                    to={`/categories/${row.slug}`}
                  >
                    All {row.count} articles{' '}
                    <ArrowRightIcon size={13} weight="bold" />
                  </Link>
                </div>
              )
            })}
          </div>
        </section>
      ) : null}

      <section className="section reveal" style={{ animationDelay: '140ms' }}>
        <div className="section-label">
          <span>Browse by product area</span>
          {index ? <span className="count">{areas.length}</span> : null}
        </div>
        {index ? (
          areaGroups.map(({ section, rows }) => (
            <div key={section.key} className="category-group">
              <h3 className="category-group-label">{section.label}</h3>
              <div className="category-grid">
                {rows.map(row => {
                  const meta = getCategoryMeta(row.slug)
                  const Icon = meta.icon
                  return (
                    <Link
                      key={row.slug}
                      to={`/categories/${encodeURIComponent(row.slug)}`}
                      className={`category-card hue-${meta.hue}`}
                    >
                      <span className="category-icon">
                        <Icon size={20} weight="duotone" />
                      </span>
                      <span className="category-card-text">
                        <span className="category-card-title">
                          {getCategoryLabel(row.slug)}
                        </span>
                        <span className="category-card-desc">
                          {meta.description}
                        </span>
                      </span>
                      <span className="category-card-count">{row.count}</span>
                    </Link>
                  )
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="category-grid">
            {Array.from({ length: 12 }).map((_, position) => (
              <div key={position} className="category-card skeleton" />
            ))}
          </div>
        )}
      </section>

      <section className="section reveal" style={{ animationDelay: '200ms' }}>
        <div className="support-strip">
          <div className="support-item">
            <ChatCircleDotsIcon size={20} weight="duotone" />
            <div>
              <strong>Ask Benny in the app</strong>
              <span>
                Your Scrambl teammate answers from this same library, in
                context.
              </span>
            </div>
            <a href={APP_URL}>Open app</a>
          </div>
          <div className="support-item">
            <LifebuoyIcon size={20} weight="duotone" />
            <div>
              <strong>Talk to support</strong>
              <span>Email us and a person will get back to you.</span>
            </div>
            <a href="mailto:support@scrambl.com">support@scrambl.com</a>
          </div>
          <div className="support-item">
            <PulseIcon size={20} weight="duotone" />
            <div>
              <strong>System status</strong>
              <span>Live uptime and incident history.</span>
            </div>
            <a href={STATUS_URL}>status.scrambl.com</a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}
